import * as React from 'react';
import * as R from 'ramda';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ROUTES } from '../constants/routes';

import { TextInput, ButtonMain, UnderlinedLink } from '../atoms';
import PasswordInput from '../molecules/PasswordInput';
import { userErrorsSelector } from '../redux/ducks/userAuth';
import { addErrorsForEmptyFields, isValidationSucceed } from '../utils/validation';
import { APIFormErrors } from '../types';
import { LogInDataType } from '../api/userAuth';

const initialErrors: Errors = {};

const LogInForm = ({ fields, setFields, onLogIn }: Props) => {
  const apiErrors: APIFormErrors = useSelector(userErrorsSelector);

  const history = useHistory();

  const [errors, setErrors] = React.useState(initialErrors);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Re-enable the button if the login attempt comes back with an error.
  React.useEffect(() => {
    if (apiErrors.nonFieldErrors || apiErrors.email || apiErrors.password) {
      setIsSubmitting(false);
    }
  }, [apiErrors]);

  const handleInputChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFields(R.assoc(name, e.target.value, fields));
    setErrors(R.dissoc(name));
  };

  const validateForm = () => {
    const newErrors = addErrorsForEmptyFields(fields, initialErrors, 'This field can not be empty');

    setErrors(newErrors);

    return isValidationSucceed(newErrors);
  };

  const handleChangePassword = () => {
    history.push(ROUTES.FORGOT_PASSWORD);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const isValid = validateForm();

    if (isValid) {
      setIsSubmitting(true);
      onLogIn();
    }
  };

  return (
    <Wrapper>
      <Form onSubmit={handleSubmit}>
        <InputBox>
          <TextInput
            placeholder="Email"
            fullWidth
            value={fields.email}
            onChange={handleInputChange('email')}
            error={!!(apiErrors.email || errors.email)}
            helperText={(apiErrors.email && apiErrors.email[0]) || errors.email}
          />
          <PasswordInput
            id="password_test"
            label="Password"
            value={fields.password}
            onChange={handleInputChange('password')}
            isValid={!errors.password && !apiErrors.password}
            helperText={errors.password}
          />
        </InputBox>
        <ActionLinkContainer>
          <ActionLink onClick={handleChangePassword}>Forgot Password?</ActionLink>
        </ActionLinkContainer>
        <Buttons>
          <LoginButton type="submit" color="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Signing In…' : 'Sign In'}
          </LoginButton>
        </Buttons>
        <Divider />
        <Register>
          New to <Name>Elite Permits</Name>?
          <RegisterLink onClick={() => history.push(ROUTES.REGISTER)}>
            Register as a new user
          </RegisterLink>
        </Register>
      </Form>

      {apiErrors.nonFieldErrors && (
        <ErrorsBlock>
          {apiErrors.nonFieldErrors}

          <ErrorHint>
            Don't have an account? <RegisterErrorLink onClick={() => history.push(ROUTES.REGISTER)}>Register as a new user</RegisterErrorLink>
          </ErrorHint>
        </ErrorsBlock>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  margin-top: var(--space-8);
`;

const Form = styled.form`
  width: 100%;
`;

const InputBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  width: 100%;
`;

const ActionLinkContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: var(--space-3);
`;

const ActionLink = styled(UnderlinedLink).attrs({
  as: 'span',
})`
  font-size: var(--fs-sm);
  color: var(--primary-600);
  cursor: pointer;

  &:hover {
    color: var(--primary-700);
  }
`;

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  margin-top: var(--space-8);
  width: 100%;
`;

const LoginButton = styled(ButtonMain)`
  width: 100%;
  min-height: 44px;
  font-size: var(--fs-lg);
  font-weight: var(--fw-semibold);
  letter-spacing: 0.3px;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);

  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: var(--neutral-200);
  margin: var(--space-6) 0;
`;

const Register = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-1);
  font-size: var(--fs-base);
  color: var(--neutral-600);
`;

const Name = styled.span`
  font-style: italic;
  font-weight: var(--fw-semibold);
  color: var(--neutral-800);
`;

const RegisterLink = styled.span`
  color: var(--primary-600);
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  margin-left: var(--space-2);

  &:hover {
    color: var(--primary-700);
  }
`;

const ErrorsBlock = styled.p`
  width: 100%;
  color: var(--danger-500);
  text-align: center;
  font-size: var(--fs-sm);
  margin-top: var(--space-4);
`;

const ErrorHint = styled.div`
  color: var(--neutral-600);
  font-size: var(--fs-sm);
  margin-top: var(--space-2);
`;

const RegisterErrorLink = styled.span`
  color: var(--primary-600);
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: var(--primary-700);
  }
`;

interface Props {
  fields: LogInDataType;
  setFields: (arg: LogInDataType) => void;
  onLogIn: () => void;
}

interface Errors {
  [s: string]: string;
  email?: string;
  password?: string;
}

export default LogInForm;
